import { avisoDeSesionDevuelta } from '../sesionSocio'
import type { ServiceWindow, SesionSocio } from '../types/portal'

/**
 * Chip de la ventana de servicio de 24 h de WhatsApp.
 *
 * Tres estados visibles (el backend ya los resolvió, ver
 * `portal_api/operacion/ventana.py`):
 *   open      → gris, informativo
 *   expiring  → ámbar, quedan ≤2 h para responder libremente
 *   closed    → ámbar, solo plantilla aprobada
 *   unknown   → el cliente nunca escribió: tampoco se puede texto libre
 *
 * Aparece en la cabecera del chat Y en cada ítem de la lista, para que el
 * asesor priorice sin abrir cada conversación.
 *
 * Con la sesión devuelta a Software dice eso y no el tiempo: la ventana es la
 * del número de Software, y mostrar «quedan 5 h» invitaba a escribir un mensaje
 * que no llega (TEST, 06-oct-2026). Ver `sesionSocio.ts`.
 */
export default function WindowChip({ window: w, compact = false, sesionSocio = null }: {
  window: ServiceWindow
  compact?: boolean
  sesionSocio?: SesionSocio | null
}) {
  const devuelta = avisoDeSesionDevuelta({ sesion_socio: sesionSocio })
  if (devuelta) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-chip border border-surface-border bg-surface-faint px-2 py-0.5 text-pill font-bold text-ink-soft"
        title={[devuelta.titulo, devuelta.porque, devuelta.queHacer].filter(Boolean).join(' ')}
      >
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
          <path d="M4.5 3 2 5.5 4.5 8M2.4 5.5h5.1a2.5 2.5 0 0 1 0 5H6"
                stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {devuelta.chip}
      </span>
    )
  }
  const cerrada = w.state === 'closed'
  const desconocida = w.state === 'unknown'
  const alerta = cerrada || desconocida || w.state === 'expiring'

  const texto =
    cerrada ? (compact ? 'Ventana cerrada' : 'Ventana 24 h cerrada')
    : desconocida ? (compact ? 'Sin respuesta' : 'El cliente aún no escribe')
    : compact ? `Quedan ${w.time_left_label}`
    : `Ventana 24 h · quedan ${w.time_left_label}`

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-chip border px-2 py-0.5 text-pill font-bold ${
        alerta
          ? 'border-warn-border bg-warn-light text-warn'
          : 'border-surface-border bg-surface text-ink-muted'
      }`}
      title={
        w.expires_at
          ? `Se cierra el ${new Date(w.expires_at).toLocaleString('es-PE')}`
          : 'Sin mensaje entrante: WhatsApp no permite texto libre'
      }
    >
      {alerta && (
        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
          <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.3" />
          <path d="M6 3.4V6l1.8 1.2" stroke="currentColor" strokeWidth="1.3"
                strokeLinecap="round" />
        </svg>
      )}
      {texto}
    </span>
  )
}
